import store from '../../store/store';
import { CENTRAL_API_BASE_URL, LOGOUT_API } from '../constants/central-api';
import { submitLogToBugsnag } from './log';
import { clearAgencyAuth } from '../../store/reducers/authSlice';
import { DEFAULT_LANGUAGE, forgetLanguage } from '../i18n/languages';
import { setAppLanguage } from '../../store/reducers/appSlice';

/** POST /api/v1/agency/employees/logout — expires the httpOnly `tara_session`. */
export const EMPLOYEE_LOGOUT_API = `${CENTRAL_API_BASE_URL}agency/employees/logout`;

/**
 * How long a revoke call may take before it is given up. Sign-out never waits
 * on it (the local state is cleared straight away); this only bounds how long
 * the request lingers.
 */
export const REVOKE_TIMEOUT_MS = 5000;

type FetchImpl = typeof fetch;

/** The global fetch, looked up when the request is made (tests swap it). */
const globalFetch: FetchImpl = (...args) => fetch(...args);

/**
 * POSTs a logout with the given bearer and the session cookie. `fetchImpl` is
 * called synchronously, so the request is on its way before the caller's next
 * statement runs (the local hard reset that wipes the tokens).
 */
function postLogout(
  url: string,
  token: string,
  fetchImpl: FetchImpl,
  timeoutMs: number
): Promise<Response> {
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : undefined;

  let request: Promise<Response>;
  try {
    request = Promise.resolve(
      fetchImpl(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // Phase B — send the agency-session cookie so the backend's
        // Set-Cookie expiry (AgencySessionCookie::forget) actually lands in
        // the browser and the cookie can't silently re-authenticate later.
        credentials: 'include',
        signal: controller?.signal,
      })
    );
  } catch (exception) {
    request = Promise.reject(exception);
  }

  return request.finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * 2026-09-07 — sign-out for AGENCY sessions (invite-only employees, and the
 * agency key a seller login also carries). `LOGOUT_API` is the seller-JWT
 * route; invite-only sessions never hold that token, so "Sign Out" only
 * cleared local state and the httpOnly cookie kept restoring the session on
 * the next visit. Hit the employee logout with the agency key (+ credentials,
 * so the Set-Cookie expiry lands) and drop the in-memory identity.
 *
 * The request is sent and the identity dropped synchronously; the returned
 * promise only reports the outcome and never rejects. Exported for tests.
 */
export function revokeAgencySession(
  agencyKey: string,
  fetchImpl: FetchImpl = globalFetch,
  timeoutMs: number = REVOKE_TIMEOUT_MS
): Promise<boolean> {
  const request = postLogout(
    EMPLOYEE_LOGOUT_API,
    agencyKey,
    fetchImpl,
    timeoutMs
  );
  store.dispatch(clearAgencyAuth());

  return request.then(
    (response) => response.ok,
    (exception) => {
      submitLogToBugsnag(
        'error',
        `Error while ending agency session: ${exception}`
      );
      return false;
    }
  );
}

/**
 * Revokes the seller JWT on the server (`POST /api/v1/logout`). Same contract
 * as `revokeAgencySession`: sent synchronously, never rejects. Deliberately
 * not routed through `asyncHandler`: its expired-token check calls back into
 * this logout, and its 401 handling would hard-reset and toast mid sign-out.
 * Exported for tests.
 */
export function revokeSellerSession(
  sellerToken: string,
  fetchImpl: FetchImpl = globalFetch,
  timeoutMs: number = REVOKE_TIMEOUT_MS
): Promise<boolean> {
  return postLogout(LOGOUT_API, sellerToken, fetchImpl, timeoutMs).then(
    async (response) => {
      if (!response?.ok) return false;
      try {
        const data = await response.json();
        return data?.success === true;
      } catch (_exception) {
        // A 2xx without a JSON body still ended the session.
        return true;
      }
    },
    (exception) => {
      submitLogToBugsnag('error', `Error while revoking token : ${exception}`);
      return false;
    }
  );
}

/**
 * Ends the session on the server and resets the language.
 *
 * Callers run `localStorageHardReset()` right after this WITHOUT awaiting it,
 * which wipes the tokens from the store. So the tokens are read and both
 * revoke requests sent before the first `await` here; nothing awaited later
 * (the lazy i18n import) can delay them or make them read a cleared store.
 * Never rejects; resolves to whether the server confirmed the revoke (`null`
 * when there was nothing to revoke).
 */
export async function tokenRevokeAtLogOutUser(): Promise<boolean | null> {
  const auth = store.getState().auth;
  const sellerToken = auth.authorization.accessToken?.sellerToken || '';
  const agencyKey = auth.agency?.accessToken || '';

  const sellerRevoke = sellerToken ? revokeSellerSession(sellerToken) : null;
  // Also alongside a seller token: a seller login carries an agency key too,
  // and the employee logout is what ends that agency session.
  const agencyRevoke = agencyKey ? revokeAgencySession(agencyKey) : null;

  // The remembered language belongs to the user signing out; the next user
  // on this browser must not start in it, and the login page shown right
  // after this logout must not be rendered in it either.
  forgetLanguage();
  store.dispatch(setAppLanguage(DEFAULT_LANGUAGE));
  // Loaded lazily: a static import would pull the i18n setup into the module
  // graph of everything that reaches this file (every API client, via
  // async-handler), including tests that mock react-i18next partially.
  try {
    const { default: i18n } = await import('../../internationalization/i18n');
    await i18n.changeLanguage(DEFAULT_LANGUAGE);
  } catch (exception) {
    submitLogToBugsnag('error', `Error while resetting language: ${exception}`);
  }

  const [sellerOk, agencyOk] = await Promise.all([sellerRevoke, agencyRevoke]);
  if (sellerRevoke) return sellerOk;
  if (agencyRevoke) return agencyOk;
  return null;
}
